/**
 * RDS-backed cart for Shipped.io
 * Fetches cart from AWS backend, not Shopify cart
 */

class RDSCart {
  constructor() {
    this.apiBase = window.ShippedioSettings?.amazonApiEndpoint?.replace(/\/+$/, '') || '';
    if (!this.apiBase) {
      // Fallback to deployed API if theme setting missing
      this.apiBase = 'https://vlkosphnv0.execute-api.me-south-1.amazonaws.com/prod/api';
      try { console.warn('RDSCart: amazonApiEndpoint missing; using default API base'); } catch (_) {}
    }
    this.customerId = window.ShippedioSettings?.customerId || null;
    // Use customer ID as session ID (no localStorage)
    this.sessionId = this.customerId ? `customer_${this.customerId}` : null;
    this.cartItems = [];
    this.currentPage = 1;
    this.pagination = null;
    this.klaviyoTrackingTimer = null; // Timer for 30-minute delay
    this.init();
  }

  init() {
    // Check for completed orders first
    this.checkForCompletedOrder();
    
    // Load cart on page load
    this.refreshCart();
    
    // Listen for cart refresh events
    document.addEventListener('cart:refresh', () => this.refreshCart());
    document.documentElement.addEventListener('cart:refresh', () => this.refreshCart());
  }

  getSessionId() {
    // Use customer ID as session (no localStorage)
    const customerId = window.ShippedioSettings?.customerId;
    return customerId ? `customer_${customerId}` : null;
  }

  async refreshCart(page = null) {
    if (page !== null) {
      this.currentPage = page;
    }
    
    try {
      const response = await fetch(`${this.apiBase}?action=get_cart&session_id=${this.sessionId}&page=${this.currentPage}&limit=15`);
      
      if (response.ok) {
        const data = await response.json();
        this.cartItems = data.items || [];
        this.pagination = data.pagination || null;
        this.renderCart();
        this.updateCartCount(data.count || 0);
        this.renderPagination();
        
        // Schedule Klaviyo tracking 30 minutes after last cart change
        if (this.cartItems.length > 0) {
          this.scheduleKlaviyoTracking();
        }
      }
    } catch (error) {
      console.error('Cart refresh error:', error);
    }
  }
  
  scheduleKlaviyoTracking() {
    // Clear any existing timer
    if (this.klaviyoTrackingTimer) {
      clearTimeout(this.klaviyoTrackingTimer);
    }
    
    // Set new timer for 1 minute (TESTING - change back to 30 minutes later)
    this.klaviyoTrackingTimer = setTimeout(() => {
      this.trackCartForAbandonment();
    }, 1 * 60 * 1000); // 1 minute for testing (change to 30 for production)
    
  }
  
  async trackCartForAbandonment() {
    // Only track if we have customer email
    const customerEmail = window.ShippedioSettings?.customerEmail;
    if (!customerEmail) {
      return;
    }
    
    // Don't track if cart is empty
    if (this.cartItems.length === 0) {
      return;
    }
    
    try {
      
      await fetch(this.apiBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'track_cart_event',
          session_id: this.sessionId,
          customer_id: this.customerId,
          customer_email: customerEmail,
          cart_items: this.cartItems
        })
      });
      
    } catch (error) {
      console.error('❌ Failed to track cart to Klaviyo:', error);
    }
  }

  renderCart() {
    const container = document.querySelector('[data-rds-cart-items]');
    const emptyState = document.querySelector('[data-rds-cart-empty]');
    const footer = document.querySelector('[data-rds-cart-footer]');
    
    if (!container) return;

    if (this.cartItems.length === 0) {
      if (emptyState) emptyState.style.display = 'block';
      container.innerHTML = '';
      if (footer) footer.style.display = 'none';
      return;
    }

    if (emptyState) emptyState.style.display = 'none';
    if (footer) footer.style.display = 'block';

    container.innerHTML = this.cartItems.map(item => this.renderCartItem(item)).join('');
    this.attachItemListeners();
    this.updateTotals();
  }

  renderCartItem(item) {
    const productData = typeof item.product_data === 'string' ? JSON.parse(item.product_data) : item.product_data;
    const image = productData.image || 'https://via.placeholder.com/100';
    const title = productData.title || 'Amazon Product';
    const amazonUrl = productData.amazon_url || `https://www.amazon.com/dp/${item.asin}`;
    
    // Prices are per-item in database, multiply by quantity for display
    const qty = parseInt(item.quantity);
    const amazonPrice = parseFloat(item.amazon_price) * qty;
    const shippingCost = parseFloat(item.shipping_cost) * qty;
    const taxCost = parseFloat(item.tax_cost) * qty;
    const customsCost = parseFloat(item.customs_cost) * qty;
    const totalPrice = parseFloat(item.total_price) * qty;
    
    return `
      <div class="rds-cart-item" data-cart-item-id="${item.id}" data-asin="${item.asin}">
        <div class="rds-cart-item__image">
          <img src="${image}" alt="${this.escapeHtml(title)}" width="80" height="80">
        </div>
        <div class="rds-cart-item__details">
          <h4 class="rds-cart-item__title">${this.escapeHtml(title)}</h4>
          <div class="rds-cart-item__meta">
            <span>ASIN: ${item.asin}</span>
            <a href="${amazonUrl}" target="_blank" rel="noopener">View on Amazon →</a>
          </div>
          <div class="rds-cart-item__pricing">
            <div><small>Amazon:</small> $${amazonPrice.toFixed(2)}</div>
            <div><small>Shipping:</small> $${shippingCost.toFixed(2)}</div>
            <div><small>Tax:</small> $${taxCost.toFixed(2)}</div>
            <div><small>Customs:</small> $${customsCost.toFixed(2)}</div>
            <div class="rds-cart-item__total"><strong>Total:</strong> $${totalPrice.toFixed(2)}</div>
          </div>
        </div>
        <div class="rds-cart-item__actions">
          <div class="rds-cart-item__quantity">
            <button class="qty-btn" data-qty-decrease data-asin="${item.asin}" data-qty="${item.quantity}">−</button>
            <input type="number" value="${item.quantity}" min="1" readonly class="qty-input">
            <button class="qty-btn" data-qty-increase data-asin="${item.asin}" data-qty="${item.quantity}">+</button>
          </div>
          <button class="rds-cart-item__remove" data-remove-item data-asin="${item.asin}">Remove</button>
        </div>
      </div>
    `;
  }

  attachItemListeners() {
    // Quantity increase
    document.querySelectorAll('[data-qty-increase]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const asin = e.target.dataset.asin;
        const currentQty = parseInt(e.target.dataset.qty);
        await this.updateQuantity(asin, currentQty + 1);
      });
    });

    // Quantity decrease
    document.querySelectorAll('[data-qty-decrease]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const asin = e.target.dataset.asin;
        const currentQty = parseInt(e.target.dataset.qty);
        if (currentQty > 1) {
          await this.updateQuantity(asin, currentQty - 1);
        }
      });
    });

    // Remove item
    document.querySelectorAll('[data-remove-item]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const asin = e.target.dataset.asin;
        await this.removeItem(asin);
      });
    });
  }

  async updateQuantity(asin, quantity) {
    try {
      const response = await fetch(this.apiBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update_quantity',
          session_id: this.sessionId,
          customer_id: this.customerId,
          customer_email: window.ShippedioSettings?.customerEmail || null,
          asin,
          quantity
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update just this item in the cart array
        const itemIndex = this.cartItems.findIndex(item => item.asin === asin);
        if (itemIndex !== -1 && data.item) {
          this.cartItems[itemIndex] = data.item;
        }
        
        // Update cart count
        if (data.cart_count !== undefined) {
          this.updateCartCount(data.cart_count);
        }
        
        // Re-render cart (faster since we already have the data)
        this.renderCart();
      }
    } catch (error) {
      console.error('Update quantity error:', error);
    }
  }

  async removeItem(asin) {
    try {
      const response = await fetch(this.apiBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'remove_from_cart',
          session_id: this.sessionId,
          customer_id: this.customerId,
          customer_email: window.ShippedioSettings?.customerEmail || null,
          asin
        })
      });

      if (response.ok) {
        await this.refreshCart();
      }
    } catch (error) {
      console.error('Remove item error:', error);
    }
  }

  async clearCart() {
    // Confirm before clearing
    if (!confirm('Are you sure you want to empty your cart? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(this.apiBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'clear_cart',
          session_id: this.sessionId,
          customer_id: this.customerId,
          customer_email: window.ShippedioSettings?.customerEmail || null
        })
      });

      if (response.ok) {
        await this.refreshCart();
      }
    } catch (error) {
      console.error('Clear cart error:', error);
    }
  }

  async checkout() {
    try {
      const response = await fetch(this.apiBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'checkout',
          session_id: this.sessionId,
          customer_id: this.customerId,
          customer_email: window.ShippedioSettings?.customerEmail || null
        })
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        console.error('Checkout failed:', errText);
        alert('Checkout failed. Please try again.');
        return;
      }

      const data = await response.json();
      if (data.success) {
        if (data.checkout_url) {
          // Store session ID in localStorage to check for completion later
          localStorage.setItem('pending_checkout_session', this.sessionId);
          window.location.href = data.checkout_url;
          return;
        }
        // If no checkout_url yet, tell user to retry; backend will generate it
      }
      alert(data.error || 'Checkout failed. Please try again.');
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Checkout failed. Please try again.');
    }
  }

  /**
   * Check if user returned from a completed checkout and clear cart
   */
  async checkForCompletedOrder() {
    const pendingSession = localStorage.getItem('pending_checkout_session');
    if (!pendingSession) return;

    // Check if we're returning from checkout (URL contains checkout success indicators)
    const urlParams = new URLSearchParams(window.location.search);
    const isCheckoutSuccess = urlParams.get('checkout') === 'success' || 
                             window.location.pathname.includes('checkout') ||
                             window.location.pathname.includes('thank_you');

    if (isCheckoutSuccess) {
      try {
        // Call backend to clear cart for this session
        await fetch(this.apiBase, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'order_completed',
            session_id: pendingSession
          })
        });
        
        // Clear the pending session
        localStorage.removeItem('pending_checkout_session');
        
        // Refresh cart to show empty state
        await this.refreshCart();
      } catch (error) {
        console.error('❌ Error clearing cart after checkout:', error);
      }
    }
  }

  updateCartCount(count) {
    const bubbles = document.querySelectorAll('.m-cart-count-bubble, [data-cart-count], [data-rds-cart-count]');
    bubbles.forEach(bubble => {
      if (count > 0) {
        bubble.textContent = count;
        bubble.style.display = 'flex';
        bubble.classList.remove('m:hidden');
      } else {
        bubble.style.display = 'none';
      }
    });
  }

  updateTotals() {
    let subtotal = 0;
    this.cartItems.forEach(item => {
      subtotal += parseFloat(item.total_price) * parseInt(item.quantity);
    });

    const subtotalEl = document.querySelector('[data-rds-cart-subtotal]');
    const totalEl = document.querySelector('[data-rds-cart-total]');
    
    if (subtotalEl) subtotalEl.textContent = `$${subtotal.toFixed(2)}`;
    if (totalEl) totalEl.textContent = `$${subtotal.toFixed(2)}`;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  renderPagination() {
    const paginationContainer = document.querySelector('[data-rds-cart-pagination]');
    if (!paginationContainer) return;

    if (!this.pagination || this.pagination.totalPages <= 1) {
      paginationContainer.innerHTML = '';
      paginationContainer.style.display = 'none';
      return;
    }

    paginationContainer.style.display = 'flex';
    paginationContainer.innerHTML = `
      <div class="rds-pagination" style="display: flex; align-items: center; justify-content: space-between; padding: 20px 0; gap: 12px;">
        <button 
          class="rds-pagination__prev" 
          ${!this.pagination.hasPrevPage ? 'disabled' : ''} 
          onclick="window.rdsCart.refreshCart(${this.currentPage - 1})"
          style="padding: 8px 16px; border: 1px solid #ddd; background: ${this.pagination.hasPrevPage ? '#fff' : '#f5f5f5'}; cursor: ${this.pagination.hasPrevPage ? 'pointer' : 'not-allowed'}; border-radius: 4px; font-size: 14px;">
          ← Previous
        </button>
        <span class="rds-pagination__info" style="font-size: 14px; color: #666;">
          Page ${this.pagination.page} of ${this.pagination.totalPages} (${this.pagination.total} items)
        </span>
        <button 
          class="rds-pagination__next" 
          ${!this.pagination.hasNextPage ? 'disabled' : ''} 
          onclick="window.rdsCart.refreshCart(${this.currentPage + 1})"
          style="padding: 8px 16px; border: 1px solid #ddd; background: ${this.pagination.hasNextPage ? '#fff' : '#f5f5f5'}; cursor: ${this.pagination.hasNextPage ? 'pointer' : 'not-allowed'}; border-radius: 4px; font-size: 14px;">
          Next →
        </button>
      </div>
    `;
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.rdsCart = new RDSCart();
  });
} else {
  window.rdsCart = new RDSCart();
}
